import React from 'react'; // eslint-disable-line no-unused-vars
import { BUTTON } from '../../../../constants';
import { Button, Input } from '../../../shared';

/**
 * DispatchActionBar - Shared component for dispatch action buttons and selection info
 *
 * Renders action buttons (Group Dispatch, Print Packing List, Create Bill, etc.)
 * and selection information display. Configurable via feature flags for vendor-specific behavior.
 *
 * @param {Object} props - Component props
 * @param {Object} props.selection - Selection state from useDispatchSelection
 * @param {Object} props.actions - Action handlers from useDispatchActions
 * @param {Object} props.paginationData - Pagination data
 * @param {Object} props.features - Vendor-specific feature flags
 * @param {boolean} props.features.showGroupDispatch - Show GROUP_DISPATCH button
 * @param {boolean} props.features.showPrintPacking - Show PRINT_PACKING_LIST button
 * @param {boolean} props.features.showCreateBill - Show CREATE_BILL button
 * @param {boolean} props.features.showCreateDispatch - Show CREATE_DISPATCH button
 * @param {boolean} props.features.showContainerInput - Show Container No input
 * @param {number} props.features.printPackingSelectableRecords - Selectable records for print mode
 * @param {number} props.selectableRecords - Total selectable records for bill mode
 * @param {number} props.createBillSelectableRecords - Selectable records for create bill mode
 */
export const DispatchActionBar = ({
  selection,
  actions,
  selectableRecords = 0,
  features = {
    showGroupDispatch: true,
    showPrintPacking: true,
    showCreateBill: false,
    showCreateDispatch: false,
    showContainerInput: false,
  },
}) => {
  const {
    billMode,
    printMode,
    createBillMode,
    poList,
    selectionMode,
    excludedIds,
    globalSelectAll,
    handleAddBill,
    handleAddPrint,
    handleCreateBillMode,
    handleSelectAllMatching,
    handleSelectAllVisible,
    selectedCount,
    totalSelectableForMode,
  } = selection || {};

  const {
    invoiceNo,
    setInvoiceNo,
    containerNo,
    setContainerNo,
    isUpdateDetailsLoading,
    isUpdateDetailsDisabled,
    isPrintLoading,
    isBillCreationLoading,
    isCreateDispatchLoading,
    handleSubmitDispatch,
    handlePrintPacking,
    handleCreateDispatchBill,
    handleUpdateDispatchDetail,
    handleCreateDispatch,
  } = actions || {};

  const isAnyModeActive = billMode || printMode || createBillMode;

  // Use memoized values from selection hook instead of recalculating
  const displayedCount = selectedCount || 0;
  const hasSelection = poList?.length > 0 || (selectionMode === 'all' && selectableRecords > 0);
  const totalForSelectAll = totalSelectableForMode || 0;

  // Check if already selected all
  const isAllSelected = selectedCount === totalForSelectAll && totalForSelectAll > 0;

  return (
    <div className="d-flex justify-content-between align-items-center mb-3">
      {/* Left side: Buttons and Record count */}
      <div className="d-flex align-items-center gap-3">
        {/* Group Dispatch Button */}
        {features.showGroupDispatch && !printMode && !createBillMode && (
          <Button
            clickHandler={handleAddBill}
            className="blue-btn d-flex align-items-center justify-content-center px-3">
            {billMode ? BUTTON.CANCEL : BUTTON.GROUP_DISPATCH}
          </Button>
        )}

        {/* Print Packing List Button */}
        {features.showPrintPacking && !billMode && !createBillMode && (
          <Button
            clickHandler={handleAddPrint}
            className="blue-btn d-flex align-items-center justify-content-center px-3">
            {printMode ? BUTTON.CANCEL : BUTTON.PRINT_PACKING_LIST}
          </Button>
        )}
        {/* Create Bill Button */}
        {features.showCreateBill && !billMode && !printMode && (
          <Button
            clickHandler={handleCreateBillMode}
            className="blue-btn d-flex align-items-center justify-content-center px-3">
            {createBillMode ? BUTTON.CANCEL : BUTTON.CREATE_BILL}
          </Button>
        )}

        {/* Select Page Button */}
        {isAnyModeActive && !globalSelectAll && (
          <Button
            clickHandler={handleSelectAllVisible}
            className="blue-btn d-flex align-items-center justify-content-center px-3">
            Select Page
          </Button>
        )}

        {/* Record count display - Page selection mode */}
        {poList?.length > 0 && selectionMode === 'page' && !globalSelectAll && (
          <>
            <span className="text-muted">
              {poList.length} {poList.length === 1 ? 'record' : 'records'} selected
            </span>
            {/* Select All link */}
            {!isAllSelected && (
              <span
                onClick={handleSelectAllMatching}
                className="text-primary cursor-pointer fw-semibold"
                role="button">
                Select All ({totalForSelectAll})
              </span>
            )}
          </>
        )}

        {/* Record count display - All selection mode */}
        {selectionMode === 'all' && displayedCount > 0 && (
          <>
            <span className="text-muted ms-2">
              {displayedCount} {displayedCount === 1 ? 'record' : 'records'} selected
            </span>
            {/* Re-select All link when some are excluded */}
            {excludedIds?.size > 0 && (
              <span
                onClick={handleSelectAllMatching}
                className="text-primary cursor-pointer fw-semibold"
                role="button">
                Select All ({totalForSelectAll})
              </span>
            )}
          </>
        )}

        {/* Create Dispatch Button (clothing only) */}
        {features.showCreateDispatch && !billMode && !printMode && !createBillMode && (
          <Button
            clickHandler={handleCreateDispatch}
            disabled={isCreateDispatchLoading}
            className={`white-btn d-flex align-items-center justify-content-center px-3 ${
              isCreateDispatchLoading ? 'disabled' : ''
            }`}>
            {isCreateDispatchLoading && (
              <span
                className="spinner-border spinner-border-sm text-blue me-2"
                role="status"
              />
            )}
            {isCreateDispatchLoading ? 'Creating...' : BUTTON.CREATE_DISPATCH}
          </Button>
        )}
      </div>

      {/* Right side: Action buttons */}
      <div className="d-flex align-items-center">
        {/* Invoice/Container inputs and action buttons for bill mode */}
        {hasSelection && !printMode && !createBillMode && (
          <>
            <Input
              type="text"
              placeholder="Invoice No"
              className="d-flex search-placeholder align-items-center justify-content-center container-input me-2"
              value={invoiceNo || ''}
              onChange={(e) => setInvoiceNo?.(e.target.value)}
            />
            {features.showContainerInput && (
              <Input
                type="text"
                placeholder="Container No"
                className="d-flex search-placeholder align-items-center justify-content-center container-input me-2"
                value={containerNo || ''}
                onChange={(e) => setContainerNo?.(e.target.value)}
              />
            )}
            <Button
              className={`blue-btn d-flex align-items-center justify-content-center px-3 me-2 ${
                isUpdateDetailsDisabled ? 'disabled' : ''
              }`}
              disabled={isUpdateDetailsDisabled}
              clickHandler={handleUpdateDispatchDetail}>
              {isUpdateDetailsLoading && (
                <span
                  className="spinner-border spinner-border-sm text-white me-2"
                  role="status"
                />
              )}
              {isUpdateDetailsLoading ? 'Updating...' : BUTTON.UPDATE_DISPATCH_DETAIL}
            </Button>
            <Button
              clickHandler={handleSubmitDispatch}
              className="blue-btn d-flex align-items-center justify-content-center me-2 px-3">
              {BUTTON.SUBMIT_DISPATCH}
            </Button>
          </>
        )}

        {/* Print button in print mode */}
        {(poList?.length > 0 || selectionMode === 'all') && printMode && (
          <Button
            clickHandler={() => handlePrintPacking?.(poList)}
            disabled={isPrintLoading}
            className={`blue-btn d-flex align-items-center justify-content-center me-2 px-3 ${
              isPrintLoading ? 'disabled' : ''
            }`}>
            {isPrintLoading && (
              <span
                className="spinner-border spinner-border-sm text-white me-2"
                role="status"
              />
            )}
            {isPrintLoading ? 'Printing...' : BUTTON.PRINT}
          </Button>
        )}

        {/* Create Bill button in createBillMode */}
        {(poList?.length > 0 || selectionMode === 'all') && createBillMode && (
          <Button
            clickHandler={handleCreateDispatchBill}
            disabled={isBillCreationLoading}
            className={`blue-btn d-flex align-items-center justify-content-center me-2 px-3 ${
              isBillCreationLoading ? 'disabled' : ''
            }`}>
            {isBillCreationLoading && (
              <span
                className="spinner-border spinner-border-sm text-white me-2"
                role="status"
              />
            )}
            {isBillCreationLoading ? 'Creating...' : BUTTON.CREATE_BILL}
          </Button>
        )}
      </div>
    </div>
  );
};
